'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import EventsTab from 'src/parts/tabs/EventsTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getGroupEvents } from 'src/api/admin/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
import { getProjects } from 'src/api/admin/projects';

export const Route = createFileRoute('/_admin/groups/$groupId/events')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const [events, types, tags, projects] = await Promise.all([
			getGroupEvents(params.groupId, { page: deps.page, sort: deps.sort }),
			getEventTypes(),
			getAllTags(),
			getProjects(),
		]);
		return { events, types: types.data, tags, projects: projects.data };
	},
	component: EventsPage,
});

function EventsPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const queryClient = useQueryClient();
	const {
		events: initialEvents,
		types: initialTypes,
		tags: initialTags,
		projects: initialProjects,
	} = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: events, refetch: refetchEvents } = useQuery({
		queryKey: ['group-events', groupId, searchParams.page, searchParams.sort],
		queryFn: () => getGroupEvents(groupId, { page: searchParams.page, sort: searchParams.sort }),
		initialData: initialEvents,
	});

	const handleReload = () => {
		refetchEvents();
	};

	const addEvent = async (data) => {
		await request.post('/api/events', data);
		queryClient.invalidateQueries({ queryKey: ['group-events', groupId] });
		queryClient.invalidateQueries({ queryKey: ['events'] });
	};

	return (
		<EventsTab
			authenticatedUser={authenticatedUser!}
			events={events}
			groupId={groupId}
			addEvent={addEvent}
			onReload={handleReload}
			types={initialTypes}
			permissions={{
				view: verifyPermission(PERMISSIONS.adminEventsView),
				create: verifyPermission(PERMISSIONS.adminEventsCreate),
				update: verifyPermission(PERMISSIONS.adminEventsEdit),
				archive: verifyPermission(PERMISSIONS.adminEventsArchive),
			}}
			tags={initialTags}
			singleGroupAsOwner={usesSubEvents}
			featureFlags={featureFlags}
		/>
	);
}
